import type { OfferDetailResult } from 'tripPlanner/api/types'
import type {
  DrawerHotel,
  DrawerHotelPrice,
} from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/HotelListingDrawer/types'
import {
  displayPricingFromDTO,
  includesTaxesAndFees,
  isTotalMode,
  pricingUnavailableReason,
  type PublicAccommodationPricing,
} from 'tripPlanner/utils/accommodationPricingDisplay'

const DEFAULT_NIGHTS = 1

export function mapImages(imageIds: Array<string>): Array<App.Image> {
  return imageIds.map((id) => ({ id }))
}

export function mapPrice(
  price?: number,
  numberOfNights?: number,
  currencyCode?: string,
): DrawerHotelPrice | undefined {
  if (price === undefined || price <= 0) {
    return undefined
  }

  return {
    total: price,
    nights: numberOfNights ?? DEFAULT_NIGHTS,
    currency: currencyCode,
  }
}

export function mapDrawerHotelPricingFields(
  pricing: PublicAccommodationPricing | undefined,
  nights?: number,
  currencyCode?: string,
): Pick<
  DrawerHotel,
  | 'price'
  | 'isTotal'
  | 'includesTaxesAndFees'
  | 'unavailableReason'
  | 'luxPlusPrice'
  | 'value'
  | 'memberValue'
  | 'discountPercent'
  | 'memberDiscountPercent'
> {
  const displayPricing = pricing ? displayPricingFromDTO(pricing) : undefined

  return {
    price: mapPrice(displayPricing?.price, nights, displayPricing?.currencyCode ?? currencyCode),
    isTotal: pricing ? isTotalMode(pricing.mode) : false,
    includesTaxesAndFees: pricing ? includesTaxesAndFees(pricing) : false,
    unavailableReason: pricingUnavailableReason(pricing),
    luxPlusPrice: displayPricing?.luxPlusPrice,
    value: displayPricing?.value,
    memberValue: displayPricing?.memberValue,
    discountPercent: displayPricing?.discountPercent,
    memberDiscountPercent: displayPricing?.memberDiscountPercent,
  }
}

export function mapOfferDetailInclusions(detail: OfferDetailResult): NonNullable<DrawerHotel['inclusions']> {
  return detail.inclusions?.map((inc, index) => ({
    id: `${detail.offerId}-inc-${index}`,
    description: inc.text,
    symbol: (inc.icon ?? undefined) as App.OfferFeatureSymbol | undefined,
    isHighlighted: inc.isHighlighted,
  })) ?? []
}

export function mapOfferDetailAmenities(detail: OfferDetailResult): DrawerHotel['amenities'] {
  return detail.amenities?.map((amenity) => ({
    name: amenity.name,
    icon: amenity.icon,
  })) ?? []
}
